/**
 * Contract predicates — the shared executable populations (issue #696, E2).
 *
 * A measure's population is a function exported here and imported by every
 * compute path that counts it. One function, one meaning; two surfaces cannot
 * disagree about who counts because there is nothing to disagree with. This is
 * the isFirmDecision pattern (formerly apps/worker/src/snapshot/compute.ts,
 * hoisted here 2026-08-05 so the canon owns it and the worker imports it) —
 * the one mechanism in this repository that has demonstrably prevented a fork.
 *
 * Pure TypeScript, no imports: this file must stay a leaf so every package
 * and surface can reach it without cycles.
 */

/** The pinned prefix the reject cascade writes into each auto-declined
 *  placement's Decline Reason. Byte-identical to the portal's literal
 *  (worker.js — the Firm Dashboard's loss classifier matches on it), and
 *  the firm-decision predicate below EXCLUDES declines carrying it — a
 *  drifted copy would silently let admin cascades count as firm decisions,
 *  so every site reads this one constant. (Canonical home since 2026-08-05;
 *  packages/shared/src/lead-action.ts re-exports it.) */
export const ADMIN_CASCADE_DECLINE_PREFIX = "Lead rejected by LegalFlare admin: ";

/** The minimal row a firm-decision judgment reads. Structural on purpose:
 *  SnapshotPlacement, adapter Placements, and any future projection qualify
 *  without importing app-side types into the canon. */
export type FirmDecisionRow = {
  readonly decision: string | null;
  readonly declineReason: string | null;
};

/** Decisions that sit at or beyond acceptance. A placement at any of these
 *  passed through the firm's own Accepted act, and nothing re-stamps
 *  `Date Responded` after accept/decline — so it still reads as the
 *  decision moment there. Mirrors POST_ACCEPT_DECISIONS in
 *  routing/eligibility.ts (portal worker.js:196) [pointer corrected
 *  2026-08-26, #764 s06 — was ":134", which is DECLINE_GATE; the mirror
 *  itself verified byte-exact across all three copies]. */
const AT_OR_PAST_ACCEPTED = new Set([
  "Accepted",
  "Placed",
  "Settled",
  "Case Closed",
  "Dropped",
]);

/** LegalFlare's own Firms row — the house record (RULED 2026-08-05, firm
 *  pass): IN the firm dimension, because LF legitimately acts as OF on its
 *  own leads (the two-party FSA case), and flagged so partner-only
 *  populations exclude it through ONE predicate instead of the private
 *  LF_FIRM_ID comparisons scattered through portal worker.js (48 comparison
 *  sites as of 2026-08-26 — the 2026-08-05 "~25" undercounted; re-count
 *  rather than trust either number). */
export const HOUSE_FIRM_ID = "rec8zKfghSmXeNJKF";

/** Is this firm the house — LegalFlare itself, not a partner practice?
 *  Partner-only measures exclude the house via this symbol (E2). */
export function isHouseFirm(firmId: string): boolean {
  return firmId === HOUSE_FIRM_ID;
}

/**
 * Is this placement a firm decision (glossary sense) — flipped to Accepted
 * or Declined by the Handling Firm's own act? (TTD's population: a jump
 * straight to Placed is a yes; a pull is not a decision.)
 *   - Declined counts unless it carries the admin-cascade prefix.
 *   - The accepted side matches any at-or-past-Accepted decision: the flip
 *     to Accepted happened even if the case has since advanced to Placed /
 *     Settled / Case Closed / Dropped, and its `Date Responded` is still
 *     the acceptance moment. Filtering on Decision = "Accepted" alone would
 *     silently drop every acceptance whose FSA landed before the photo.
 *   - Pulled (an operator removal) never counts.
 */
export function isFirmDecision(p: FirmDecisionRow): boolean {
  if (p.decision === null) return false;
  if (AT_OR_PAST_ACCEPTED.has(p.decision)) return true;
  if (p.decision === "Declined") {
    return !(p.declineReason ?? "").startsWith(ADMIN_CASCADE_DECLINE_PREFIX);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Write signatures on Placements.Placed Date (#890).
// Values only, no imports — this file stays a leaf. The STORY of each is one
// provenance entry (provenance.ts placed_date_migration_stamp,
// placed_date_fill_0000); these are the executable forms the segments name and
// every counter imports, the isFirmDecision pattern.
// ---------------------------------------------------------------------------

/** The instant the 2026-04 migration wrote onto every imported Placement's
 *  Placed Date where no signing date was recoverable — one value on every
 *  such row (#890 s01 F15b). It is 2026-04-27 in UTC and 2026-04-26 in
 *  Pacific, so no day name serves both readers; the signature is the instant.
 *  Story: provenance.ts placed_date_migration_stamp. */
export const PLACED_MIGRATION_STAMP_INSTANT = "2026-04-27T05:29:36.000Z";

/** The stock "Placements placed": every Decision at or past Placed — the total
 *  that contains placement_placed's segments (facts.ts containedIn), a state
 *  read, not the dated fact. A Dropped Placement reached Placed and is in it;
 *  a lifetime "placed" total on the glass draws this set, and a placed-by-day
 *  line draws the fact (#890 plan 14f). reconcile-facts.py carries its own copy
 *  (a third derivation shares no code). */
export const AT_OR_PAST_PLACED: ReadonlySet<string> = new Set(["Placed", "Settled", "Case Closed", "Dropped"]);

/** Is this Placed Date the migration's import stamp, not a signing? */
export function isPlacedMigrationStamp(placedDate: string | null, _pacificDay?: string | null): boolean {
  return placedDate === PLACED_MIGRATION_STAMP_INSTANT;
}

/** The Pacific day before which migration 0000's fill wrote midnight values
 *  (provenance.ts placed_date_fill_0000). Midnight values on or after it are
 *  named on the fact and counted. */
const PLACED_DATE_FILL_BEFORE_PACIFIC_DAY = "2026-04-01";

/** Is this Placed Date migration 0000's fill — a midnight time part on a
 *  Pacific day before the epoch — taken from a review, creation or id date,
 *  never a signing? Evidence-backed values carry T20:00:00.000Z and live
 *  uploads the upload instant. `pacificDay` is the caller's dateInPT(value)
 *  (packages/shared date.ts) — passed in, not recomputed here, so this leaf
 *  imports nothing and the Pacific conversion has one home. */
export function isPlacedDateFill(placedDate: string | null, pacificDay: string | null): boolean {
  if (placedDate === null || pacificDay === null) return false;
  if (!placedDate.endsWith("T00:00:00.000Z")) return false;
  return pacificDay < PLACED_DATE_FILL_BEFORE_PACIFIC_DAY;
}
